import { selectFeatureFlags } from 'core/userContext';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { CollapsibleContent } from 'ui/collapsibleContent';
import { colors } from 'ui/theme';
import { HelpToggletip } from 'ui/tooltip';
import { sortObjectListByKey } from 'utils/arrayHelpers';
import { SMS_SEGMENTS_RECEIVED, SMS_SEGMENTS_SENT } from '../../../constants';
import { useBillingQueryContext } from '../../../contexts';
import { OrganizationGlobalSmsMetric } from '../../../types';
import {
  isFirstPartyMetric,
  isOrgGlobalSmsCountryMetric,
  mapMetricsByCountry,
} from '../../../utils';
import {
  ColumnHeading,
  ShowMoreButton,
  ShowMoreFooter,
  TitleWrapper,
} from './GlobalSmsCard.styled';
import { GlobalSmsExportProjectUsageButton } from './GlobalSmsExportProjectUsageButton';
import { GlobalSmsRow } from './GlobalSmsRow';
import { LoadingCard } from '../LoadingCard';
import { MetricTile } from '../MetricTile';
import {
  Container,
  Header,
  MainContent,
  Marquee,
  RowsWrapper,
  TableHeading,
  TableHeadingRow,
  Title,
} from '../styled';
import { GLOBAL_SMS_CARD_IDS, GLOBAL_SMS_CARD_TEXT } from './constants';

export const GlobalSmsCard = () => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const { pickOrgMetrics, pickOrgValue, isLoading } = useBillingQueryContext();
  const { GlobalSmsFirstPartyUI } = useSelector(selectFeatureFlags);

  const smsSegmentsSentValue = pickOrgValue(SMS_SEGMENTS_SENT, isFirstPartyMetric);
  const smsSegmentsReceivedValue = pickOrgValue(SMS_SEGMENTS_RECEIVED, isFirstPartyMetric);
  const ms = pickOrgMetrics(SMS_SEGMENTS_SENT, isOrgGlobalSmsCountryMetric) || [];
  const metrics = sortObjectListByKey(
    mapMetricsByCountry(ms as OrganizationGlobalSmsMetric[]),
    'SmsSegmentsSent',
    true,
  );

  const firstThreeMetrics = metrics.slice(0, 3);
  const remainingMetrics = metrics.slice(3);

  const toggle = () => setIsCollapsed(!isCollapsed);

  if (!GlobalSmsFirstPartyUI) return null;

  return isLoading ? (
    <LoadingCard />
  ) : (
    <Container data-test={GLOBAL_SMS_CARD_IDS.cardId}>
      <Header>
        <TitleWrapper>
          <Title>{GLOBAL_SMS_CARD_TEXT.cardTitle}</Title>
          <HelpToggletip
            tooltipContent={GLOBAL_SMS_CARD_TEXT.description}
            iconProps={{
              color: colors.textSecondary,
            }}
          />
        </TitleWrapper>
        <div>
          <GlobalSmsExportProjectUsageButton />
        </div>
      </Header>
      <Marquee>
        <MetricTile
          label={GLOBAL_SMS_CARD_TEXT.smsSegmentsSentName}
          tooltip={GLOBAL_SMS_CARD_TEXT.smsSegmentsSentDescription}
          value={smsSegmentsSentValue}
        />
        <MetricTile
          label={GLOBAL_SMS_CARD_TEXT.smsSegmentsReceivedName}
          value={smsSegmentsReceivedValue}
        />
      </Marquee>
      <MainContent>
        {metrics.length > 0 && (
          <TableHeadingRow>
            <TableHeading>SMS usage by country</TableHeading>
            <ColumnHeading>
              {/**
               * SmsSent is not currently available for GlobalSMS, but may be added in future.
               * Leaving the ColumnHeading to retain the spacing.
               * */}
              {/* SMS sent */}
            </ColumnHeading>
            <ColumnHeading>SMS segments sent</ColumnHeading>
          </TableHeadingRow>
        )}
        <RowsWrapper>
          {firstThreeMetrics.map((metric) => (
            <GlobalSmsRow key={metric.label} metric={metric} />
          ))}
        </RowsWrapper>
        <CollapsibleContent isCollapsed={isCollapsed}>
          <RowsWrapper>
            {remainingMetrics.map((metric) => (
              <GlobalSmsRow key={metric.label} metric={metric} />
            ))}
          </RowsWrapper>
        </CollapsibleContent>
        {remainingMetrics?.length > 0 && (
          <ShowMoreFooter>
            <ShowMoreButton onClick={toggle}>
              {`Show ${isCollapsed ? 'more' : 'less'}`}
            </ShowMoreButton>
          </ShowMoreFooter>
        )}
      </MainContent>
    </Container>
  );
};
